/* -----------------Helpers & Hooks--------------- */
import { calcScore, getCurrentDate, getPlatform } from "@memorang/helpers";
import { useTranslation } from "react-i18next";

import {
	type Confidence,
	type RecordMixedItemInput,
	SessionConfigSchema,
} from "@memorang/types";
/* -----------------Types--------------- */
import type { SessionState } from "../../hooks/types";

import { appConfigIds } from "@constants/common";
import {
	liveSessionEnabled,
	proethicaLiveSessionConfig,
} from "@constants/demo";
import { events } from "@constants/tracking";
import type { DailyProgressState } from "@features/dashboard";
import {
	AI_SUBMITTABLE_VARIANTS,
	MAPPED_ITEM_TYPES,
	NO_RESPONSE_RENDERABLE_TYPES,
} from "@features/session/constants";
import { isAiSimulationItem } from "@features/session/helpers";
import { getNormalizedScore } from "@features/session/helpers/aiFeedback";
import { buildType } from "@helpers/expo-extras";
import type {
	AIFeedbackResponse,
	AIResponsePayload,
	Choice,
	RenderableItemTypes,
} from "@memorang/types/src/session/new-session";
import { trackCustomEvent } from "analytics";
import { trackAnswerEvent } from "../../api/mutations";
import type { TrackAnswerEventRequest } from "../../api/types";
import { useEMRStore } from "../../components/emr-card/useEMRStore";
/* -----------------Child components--------------- */
import { recordSummativeTestEvents } from "../../relay/RecordSummativeTestEvents";

const isSataAnswerCorrect = (
	choices: Choice[],
	selectedChoiceIds?: string[],
) => {
	const numRequiredAnswers = choices.filter(
		({ isCorrect }) => isCorrect,
	).length;
	const numCorrectSelectedChoiceIds = selectedChoiceIds
		? selectedChoiceIds.filter((selectedChoiceId) => {
				const selectedChoice = choices.find(
					(item) => item.id === selectedChoiceId,
				);
				return selectedChoice ? selectedChoice.isCorrect : false;
			}).length
		: 0;
	const numCorrectChoiceIds = choices.filter(
		({ isCorrect }) => isCorrect,
	).length;

	const isCorrect = numRequiredAnswers
		? numCorrectSelectedChoiceIds >= numRequiredAnswers &&
			selectedChoiceIds?.length === numCorrectSelectedChoiceIds
		: numCorrectChoiceIds === numCorrectSelectedChoiceIds;

	return isCorrect;
};
const getShouldShowConfidencesSubmitButtons = (sessionState: SessionState) => {
	const {
		itemStates,
		answers,
		sessionItems,
		currentItemIndex,
		isSummativeTest,
	} = sessionState;

	if (isSummativeTest) {
		return false;
	}
	const { id, metadata } = sessionItems[currentItemIndex];

	const mappedType = MAPPED_ITEM_TYPES[metadata?.type!];
	const isOptionlessExplore =
		mappedType === "EXPLORE_ACCORDION" &&
		!sessionItems[currentItemIndex].content?.choices?.length;
	if (
		NO_RESPONSE_RENDERABLE_TYPES.includes(mappedType) ||
		isOptionlessExplore
	) {
		return false;
	}

	const isSequence = metadata?.type === "MCOS";
	// const currentItem = sessionItems[currentItemIndex];
	// const { metadata } = currentItem;
	// const itemType = metadata?.type;

	const currentItemState = itemStates?.get(id!);
	const currentAnswer = answers?.get(id!);

	// const numMinChoicesRequired = currentItem.response?.numChoicesRequired?.min;

	const selectedChoiceIds = currentItemState?.selectedChoiceIds || [];

	const showConfidenceSubmitButtons = selectedChoiceIds?.length > 0;

	return isSequence
		? !currentAnswer
		: showConfidenceSubmitButtons && !currentAnswer;
};

const getNextButtonDisabled = ({
	mappedItemType,
	selectedChoiceIds = [],
	// minChoiceCount = 0,
	// maxChoiceCount = 0,
}: {
	mappedItemType?: RenderableItemTypes;
	selectedChoiceIds: string[];
	// minChoiceCount?: number;
	// maxChoiceCount?: number;
}) => {
	if (mappedItemType !== "SATA" || !selectedChoiceIds.length) {
		return false;
	}

	// const isMinChoiceCountValid = !!(
	// 	minChoiceCount && selectedChoiceIds.length < minChoiceCount
	// );
	// const isMaxChoiceCountValid = !!(
	// 	maxChoiceCount && selectedChoiceIds.length < maxChoiceCount
	// );

	return false;
};
const getNextButtonStates = (
	sessionState: SessionState,
	currentItemIndex: number,
) => {
	const { t } = useTranslation();
	const { sessionItems, itemStates, inReviewMode } = sessionState;

	const currentItem = sessionItems[currentItemIndex];
	const { metadata, response, id } = currentItem;

	const type = metadata?.type;
	const minChoiceCount = response?.numChoicesRequired?.min;
	const _maxChoiceCount = response?.numChoicesRequired?.max;
	const currentItemState = itemStates?.get(id!);

	const mappedType = MAPPED_ITEM_TYPES[type!];
	const isSata = mappedType === "SATA";
	const isOptionlessExplore =
		mappedType === "EXPLORE_ACCORDION" && !currentItem.content?.choices?.length;
	const isNoResponseType =
		NO_RESPONSE_RENDERABLE_TYPES.includes(mappedType) || isOptionlessExplore;

	const selectedChoiceIds = currentItemState?.selectedChoiceIds || [];

	const getIsSelected = () => {
		if (inReviewMode || isNoResponseType) {
			return true;
		}
		if (isSata) {
			return (
				selectedChoiceIds?.length >= (minChoiceCount || 0) ||
				currentItemState?.answered
			);
		}
		return (
			(selectedChoiceIds && selectedChoiceIds.length > 0) ||
			(currentOrders && currentOrders.length > 0) ||
			currentItemState?.answered
		);
	};

	const currentOrders = currentItemState?.currentOrders;

	const isSelected = getIsSelected();

	const disabled = getNextButtonDisabled({
		mappedItemType: MAPPED_ITEM_TYPES[type!],
		selectedChoiceIds: currentItemState?.selectedChoiceIds || [],
		// minChoiceCount,
		// maxChoiceCount,
	});

	return {
		text: isSelected
			? t("session.navigation.next")
			: t("session.navigation.skip"),
		showButtonNavSkip: !(isSelected || inReviewMode),
		disabled,
	};
};

// Summative tests answer via `itemStates` selections rather than the
// confidence-driven `answers` map, so finishing routes to the report when any
// item carries a selection.
const hasAnySummativeSelection = ({
	isSummativeTest,
	itemStates,
}: SessionState): boolean =>
	Boolean(isSummativeTest) &&
	Array.from(itemStates?.values() ?? []).some(
		(itemState) => (itemState.selectedChoiceIds?.length ?? 0) > 0,
	);

// A content-only session (e.g. the intro module, which has no knowledge check)
// has nothing to answer, so finishing should route straight to the report
// rather than prompting "you haven't answered any questions yet".
const sessionHasAnswerableItems = ({ sessionItems }: SessionState): boolean =>
	sessionItems.some(
		(item) =>
			isAiSimulationItem(item) ||
			AI_SUBMITTABLE_VARIANTS.includes(item.metadata?.type ?? "") ||
			(item.content?.choices?.length ?? 0) > 0,
	);

const getShowButtonNavFinish = (
	{
		answers,
		sessionItems,
		inReviewMode,
		isSummativeTest,
		itemStates,
	}: SessionState,
	currentItemIndex: number,
) => {
	const currentItem = sessionItems[currentItemIndex];
	const { id } = currentItem;
	const sessionLength = sessionItems.length;

	// Summative items don't record an `answers` entry (no per-item submit), so a
	// selection captured in `itemStates` is what turns the last item's Next into
	// Finish.
	const responded = isSummativeTest
		? (itemStates?.get(id!)?.selectedChoiceIds?.length ?? 0) > 0
		: answers?.get(id!) != null;

	return (responded || inReviewMode) && currentItemIndex === sessionLength - 1;
};

export const getSessionConfig = (sessionConfig?: string) => {
	return sessionConfig
		? SessionConfigSchema.parse(JSON.parse(sessionConfig))
		: undefined;
};

const calculateAndUpdateProgress = (state: SessionState, forward?: boolean) => {
	const { updateProgress, currentProgress, numGoal } = state;

	const finalProgress = currentProgress;

	let nextProgress = 0;

	if (forward) {
		const newProgress = finalProgress + 1 / numGoal!;
		nextProgress = newProgress;
		updateProgress(newProgress);
	} else {
		const newProgress = finalProgress - 1 / numGoal!;
		updateProgress(newProgress);
	}
	return nextProgress;
};

const handleSendSummativeAnswerEvent = (
	state: SessionState,
	currentItemIndex: number,
	userId: string,
	startTime: Date,
) => {
	const {
		id: sessionId,
		timeElapsed,
		sessionTime,
		sessionItems,
		itemStates,
		updateAnswerEventInProgress,
	} = state;

	const currentItem = sessionItems[currentItemIndex];

	const choices = currentItem.content?.choices ?? [];
	const currentItemState = itemStates?.get(currentItem.id!);
	const selectedChoiceIds = currentItemState?.selectedChoiceIds;
	const timeTaken = Math.min(
		Math.round((Date.now() - startTime.getTime()) / 1000),
		300,
	);

	const itemType = currentItem.metadata?.type;
	const mappedItemType = MAPPED_ITEM_TYPES[itemType!];

	const numDistractors = choices ? choices.length - 1 : 0;

	const correctAnswer = choices?.find((item) => item.isCorrect);
	const answerId = correctAnswer?.id;
	const isSata = mappedItemType === "SATA";
	const isCorrect = isSata
		? isSataAnswerCorrect(choices!, selectedChoiceIds)
		: selectedChoiceIds?.includes(answerId!);
	const score = isCorrect ? 100 : 0;

	const answerState = {
		selectedChoiceIds: selectedChoiceIds ? selectedChoiceIds : [],
		score,
		timeTaken: Math.round(timeTaken),
		confidence: 0,
		numDistractors,
		answerText: "",
	};
	const summativeTestItemState: RecordMixedItemInput = {
		sessionId: sessionId.toString(),
		sessionTime: timeElapsed + sessionTime,
		itemId: currentItem.id!.toString(),
		userId,
		answers: answerState,
		variant: itemType!,
	};

	recordSummativeTestEvents(summativeTestItemState).then(() => {
		updateAnswerEventInProgress(false);
	});
};

const handlePostAnswer = (
	state: SessionState,
	startTime: Date,
	confidence: Confidence,
	dailyProgressState: DailyProgressState,
	examId?: string,
	mappedItemType?: RenderableItemTypes,
	userId?: number,
	submitAnswer = true,
	aiFeedback?: AIFeedbackResponse,
	totalScore?: number,
	slideshowDurationInSeconds?: number,
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
) => {
	const {
		currentItemIndex,
		sessionItems,
		itemStates,
		id: sessionId,
		updateAnswers,
		updateItemStates,
		updateAnswerEventInProgress,
		isRecommendedTask,
		isDiagnostic,
	} = state;

	const { dailyProgressByData, setDailyProgressByDate } = dailyProgressState;

	const submitUserData = useEMRStore.getState().submitUserData;

	const currentDate = getCurrentDate();
	const key = `${examId}-${currentDate}`;

	const currentDailyProgress = dailyProgressByData?.[key];

	const timeTaken = Math.min(
		Math.round((Date.now() - startTime.getTime()) / 1000),
		300,
	);

	const currentItem = sessionItems[currentItemIndex];
	const { id: currentItemId, content } = currentItem;

	const choices = content?.choices ?? [];
	const currentItemState = itemStates?.get(currentItemId!);

	const correctChoice = choices?.find((item) => item.isCorrect);
	const answerId = correctChoice?.id;
	const correctChoiceIds = choices
		?.filter((item) => item.isCorrect)
		.map((item) => item.id);

	const selectedChoiceIds = currentItemState?.selectedChoiceIds;

	const payload = {
		stem: currentItem.content?.stem,
		scenario: currentItem.stimulus?.content?.passage?.paragraphs
			?.map((item) => item.text)
			.join("\n"),
		selectedChoices: selectedChoiceIds
			?.map((item) => choices?.find((choice) => choice.id === item)?.text)
			.join("\n"),
	};

	const numDistractors = choices ? choices.length - 1 : 0;

	const correctAnswerOrders = currentItem.response?.correctResponse?.map(
		(choice) => choice.id,
	);

	const isSequence = mappedItemType === "SEQUENCE";

	const selectedChoiceItemId = selectedChoiceIds?.[0];
	const isSata = mappedItemType === "SATA";
	const isFillInBlank = mappedItemType === "FILL_IN_THE_BLANK";

	const getIsCorrect = () => {
		if (isSequence) {
			return correctAnswerOrders?.every((order) =>
				currentItemState?.currentOrders?.includes(order),
			);
		}
		if (isSata) {
			return isSataAnswerCorrect(choices!, selectedChoiceIds);
		}
		if (isFillInBlank) {
			return (
				selectedChoiceIds?.[0]?.trim()?.toLowerCase() ===
				correctChoice?.text?.trim()?.toLowerCase()
			);
		}
		return selectedChoiceItemId === answerId;
	};
	const isCorrect = getIsCorrect();

	const hints = currentItemState?.hints?.length
		? currentItemState?.hints
		: null;

	const sessionMeta = {
		[currentItemId!]: {
			lastTime: Date.now(),
			numSuccessAttempts: isCorrect ? 1 : 0,
			numTotalAttempts: 1,
		},
	};
	const { score } = calcScore(
		confidence,
		Boolean(isCorrect),
		timeTaken,
		slideshowDurationInSeconds ?? 0,
		currentItem,
		sessionMeta,
		"practice",
	);

	if (currentDailyProgress && isRecommendedTask && score > 90) {
		const { numCompleted, numGoal } = currentDailyProgress;
		const updatedProgress = {
			[key]: {
				numCompleted: (numCompleted || 0) + 1,
				numGoal,
				status: "ACTIVE" as const,
			},
		};
		setDailyProgressByDate(updatedProgress);
	}

	const itemType = currentItem.metadata?.type;
	const isAiSubmittable = AI_SUBMITTABLE_VARIANTS.includes(itemType!);

	const goldStandard = currentItem.response?.goldStandard;

	if (!isAiSubmittable && goldStandard) {
		submitUserData(JSON.stringify(payload));
	}
	const currentAnswer = {
		timeTaken,
		confidence,
		correct: isCorrect,
		score,
		selectedChoiceIds,
	};

	updateAnswers(currentItemId!, currentAnswer);
	updateItemStates({
		itemId: currentItemId!,
		selectedChoiceId: selectedChoiceItemId!,
		correctAnswerIds: correctChoiceIds,
		scores: [score],
		answered: true,
	});
	updateAnswerEventInProgress(true);

	if (!submitAnswer) {
		return;
	}

	const aiResponse: AIResponsePayload = {
		feedback: aiFeedback?.explanation ?? "",
		subskillAssessments:
			aiFeedback?.subskillAssessments?.map((subskill) => ({
				...subskill,
				maxScore: totalScore!,
			})) ?? [],
		ai_run_id: aiFeedback?.runDetails?.runId ?? "",
		ai_thread_id: aiFeedback?.runDetails?.threadId ?? "",
		metadata: {
			assistantId: aiFeedback?.runDetails?.assistantId ?? "",
		},
		answer: aiFeedback?.answer ?? "",
	};

	const aiNormalizedScore = getNormalizedScore(
		aiFeedback?.score ?? 0,
		totalScore!,
	);

	// Live ProEthica answer-events must reference the same session context the
	// session was created under (per-module collectionId 783-790, demo project),
	// not the app's default collection - else the event mismatches its session.
	const live = liveSessionEnabled(examId);
	const collectionId =
		live && state.collectionId
			? state.collectionId
			: appConfigIds[buildType].collectionId;
	const projectId = live
		? proethicaLiveSessionConfig.projectId
		: appConfigIds[buildType].projectId;
	const organizationId = live
		? proethicaLiveSessionConfig.organizationId
		: appConfigIds[buildType].organizationId;
	const trackAnswerEventRequest: TrackAnswerEventRequest = {
		itemId: currentItemId!,
		itemVersionId: currentItem.versionId || 1,
		sessionId,
		collectionId,
		userId: userId!,
		projectId,
		organizationId,
		sessionType: live || isDiagnostic ? "SUMMATIVE" : "FORMATIVE",
		itemDetails: {
			type: "ANSWER_EVENT",
			itemType: "QUESTION",
			variant: itemType!,
		},
		response: {
			selectedChoiceIds: selectedChoiceIds!,
			confidence,
			chosenAnswerId: selectedChoiceItemId!,
			correctness: Boolean(isCorrect),
		},
		performance: {
			timeTaken,
			score: isAiSubmittable ? aiNormalizedScore : score || 0,
			xp: 0,
			points: aiFeedback?.score ?? 0,
		},
		settings: {
			shuffled: false,
			numDistractors,
		},
		userInteraction: {
			highlight: "",
			marked: false,
			hints: hints || [],
		},
		platform: getPlatform(),
		...(isAiSubmittable && aiResponse ? { aiResponse } : {}),
	};
	trackCustomEvent({
		eventName: events.sessionItemResponseSubmitted,
		confidence,
		itemId: currentItemId,
		sessionId,
		timeTaken,
		aiFeedback: JSON.stringify(aiFeedback),
	});
	if (!live) {
		updateAnswerEventInProgress(false);
		return;
	}

	trackAnswerEvent(trackAnswerEventRequest).then(() => {
		updateAnswerEventInProgress(false);
	});
};

type ShowAiSubmitResponseButtonParams = {
	sessionState: SessionState;
	currentItemIndex: number;
	currentUserResponse?: unknown;
	currentAudioResponse?: unknown;
	shouldValidateAudio?: boolean;
};

export const getShowSubmitAnswerAIFeedbackButton = ({
	sessionState,
	currentItemIndex,
	currentUserResponse,
	currentAudioResponse,
	shouldValidateAudio,
}: ShowAiSubmitResponseButtonParams) => {
	const { answers, sessionItems } = sessionState;
	const currentItem = sessionItems[currentItemIndex];
	const currentAnswer = answers?.get(currentItem.id!);

	const itemType = currentItem.metadata?.type;

	const isAiSubmittable = AI_SUBMITTABLE_VARIANTS.includes(itemType!);

	const baseShowCondition = !currentAnswer && isAiSubmittable;

	const shouldShowSubmitAnswerAIFeedbackButton =
		baseShowCondition &&
		Boolean(currentUserResponse) &&
		(shouldValidateAudio ? Boolean(currentAudioResponse) : true);

	return {
		disabled: currentAnswer != null,
		show: shouldShowSubmitAnswerAIFeedbackButton,
	};
};

export {
	getShouldShowConfidencesSubmitButtons,
	getNextButtonStates,
	calculateAndUpdateProgress,
	getShowButtonNavFinish,
	handlePostAnswer,
	handleSendSummativeAnswerEvent,
	hasAnySummativeSelection,
	sessionHasAnswerableItems,
};
